import React, { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp, TrendingDown, Calendar, Globe, ExternalLink, Heart, MessageCircle, Share2, BarChart3, Image, Smile, Activity, X } from 'lucide-react';
import StockChart from './StockChart';
import WatchlistButton from './WatchlistButton';
import UserProfilePopup from './UserProfilePopup';
import NewsFeed from './NewsFeed';
import CompanyLogo from './CompanyLogo';
import { auth, db } from '../firebase/config';
import { collection, addDoc, getDocs, query, where, orderBy, updateDoc, doc, arrayUnion, arrayRemove, serverTimestamp, getDoc } from 'firebase/firestore';
import { canCreatePost, canLikePost, canDeletePost, USER_ROLES } from '../utils/roles';
import { getDefaultAvatar } from '../utils/avatars';

export default function StockDetail({ ticker, onBack }) {
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [news, setNews] = useState([]);
  const [price, setPrice] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);
  const [error, setError] = useState(null);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [newIdea, setNewIdea] = useState('');
  const [newIdeaSentiment, setNewIdeaSentiment] = useState('bullish');
  const [newPostImages, setNewPostImages] = useState([]);
  const [newPostFeeling, setNewPostFeeling] = useState('');
  const [newPostActivity, setNewPostActivity] = useState('');
  const [showFeelingPicker, setShowFeelingPicker] = useState(false);
  const [showActivityPicker, setShowActivityPicker] = useState(false);
  const [showNewPost, setShowNewPost] = useState(false);
  const [userRole, setUserRole] = useState(USER_ROLES.GUEST);
  const [userAvatar, setUserAvatar] = useState(getDefaultAvatar());
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showProfilePopup, setShowProfilePopup] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const feelings = [
    { emoji: '😊', label: '행복해요' },
    { emoji: '😍', label: '사랑해요' },
    { emoji: '😂', label: '웃겨요' },
    { emoji: '🥰', label: '설레요' },
    { emoji: '😎', label: '멋져요' },
    { emoji: '🤗', label: '감사해요' },
    { emoji: '😢', label: '슬퍼요' },
    { emoji: '😤', label: '화나요' },
    { emoji: '😴', label: '피곤해요' },
    { emoji: '🤔', label: '생각중이에요' },
    { emoji: '😋', label: '맛있어요' },
    { emoji: '🎉', label: '축하해요' },
    { emoji: '😌', label: '평화로워요' },
    { emoji: '😆', label: '신나요' },
    { emoji: '🥳', label: '파티해요' },
    { emoji: '😇', label: '천사같아요' },
    { emoji: '🤩', label: '감동했어요' },
    { emoji: '😱', label: '놀랐어요' },
    { emoji: '🥺', label: '애틋해요' },
    { emoji: '😭', label: '울어요' },
    { emoji: '😅', label: '민망해요' },
    { emoji: '🤤', label: '군침도네요' }
  ];

  const activities = [
    { emoji: '✈️', label: '여행 중' },
    { emoji: '🏋️', label: '운동 중' },
    { emoji: '📚', label: '공부 중' },
    { emoji: '🍽️', label: '식사 중' },
    { emoji: '☕', label: '카페에서' },
    { emoji: '🎮', label: '게임 중' },
    { emoji: '🎬', label: '영화 관람 중' },
    { emoji: '🎵', label: '음악 듣는 중' },
    { emoji: '🛒', label: '쇼핑 중' },
    { emoji: '🏠', label: '집에서' },
    { emoji: '💼', label: '일하는 중' },
    { emoji: '🚗', label: '이동 중' },
    { emoji: '🎨', label: '그림 그리는 중' },
    { emoji: '🍳', label: '요리하는 중' },
    { emoji: '📖', label: '독서 중' },
    { emoji: '🧘', label: '명상 중' },
    { emoji: '🏃', label: '달리는 중' },
    { emoji: '🎤', label: '노래하는 중' },
    { emoji: '💻', label: '코딩 중' },
    { emoji: '🎯', label: '목표 달성 중' },
    { emoji: '🌅', label: '산책 중' },
    { emoji: '🛌', label: '휴식 중' }
  ];

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const imageUrls = files.map(file => URL.createObjectURL(file));
    setNewPostImages(prev => [...prev, ...imageUrls]);
  };

  const removeImage = (index) => {
    setNewPostImages(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    // Debug log on component mount
    console.log('[INIT] StockDetail component mounted for ticker:', ticker);
    console.log('[INIT] Firebase auth instance:', auth);
    console.log('[INIT] Firebase db instance:', db);
    console.log('[INIT] Current user:', auth.currentUser);
    loadUserRole();
  }, []);

  useEffect(() => {
    if (ticker) {
      loadCompanyData();
      loadNews();
      loadPrice();
      loadPosts();
    }
  }, [ticker]);

  const loadUserRole = async () => {
    const user = auth.currentUser;
    console.log('🔍 [StockDetail.loadUserRole] currentUser:', user?.uid || 'null');
    
    if (!user) {
      console.log('🔍 [StockDetail.loadUserRole] No user - setting GUEST');
      console.log('🔍 [StockDetail.loadUserRole] USER_ROLES.GUEST:', USER_ROLES.GUEST);
      setUserRole(USER_ROLES.GUEST);
      setUserAvatar(getDefaultAvatar());
      return;
    }

    try {
      console.log('🔍 [StockDetail.loadUserRole] Fetching user doc for:', user.uid);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const role = userData.userRole || USER_ROLES.MEMBER;
        console.log('🔍 [StockDetail.loadUserRole] User doc exists, userData.userRole:', userData.userRole);
        console.log('🔍 [StockDetail.loadUserRole] Setting role to:', role);
        setUserRole(role);
        setUserAvatar(userData.avatar || getDefaultAvatar());
      } else {
        console.log('🔍 [StockDetail.loadUserRole] User doc does NOT exist - setting MEMBER');
        setUserRole(USER_ROLES.MEMBER); // Default for authenticated users
        setUserAvatar(getDefaultAvatar());
      }
    } catch (error) {
      console.error('🔍 [StockDetail.loadUserRole] Error:', error);
      console.log('🔍 [StockDetail.loadUserRole] Setting MEMBER due to error');
      setUserRole(USER_ROLES.MEMBER); // Default on error
      setUserAvatar(getDefaultAvatar());
    }
  };

  const handleAvatarClick = (userId) => {
    if (userId) {
      setSelectedUserId(userId);
      setShowProfilePopup(true);
    }
  };

  const handleCloseProfilePopup = () => {
    setShowProfilePopup(false);
    setSelectedUserId(null);
  };

  const loadCompanyData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/companies/${ticker}`);
      if (!response.ok) {
        console.warn(`Company API returned ${response.status} for ${ticker}`);
        setError('Company not found');
        return;
      }
      const data = await response.json();
      if (data.success) {
        setCompany(data.company);
      } else {
        setError('Company not found');
      }
    } catch (error) {
      console.error('Error loading company:', error);
      setError('Failed to load company data');
    }
  };

  const loadNews = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/news/${ticker}`);
      if (!response.ok) {
        console.warn(`News API returned ${response.status} for ${ticker}`);
        setNews([]);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setNews(data.news || []);
      }
    } catch (error) {
      console.error('Error loading news:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPrice = async () => {
    setPriceLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/price/${ticker}`);
      if (!response.ok) {
        console.warn(`Price API returned ${response.status} for ${ticker}`);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setPrice(data.data);
      }
    } catch (error) {
      console.error('Error loading price:', error);
    } finally {
      setPriceLoading(false);
    }
  };

  const loadPosts = async () => {
    setPostsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/posts/${ticker}`);
      if (!response.ok) {
        console.warn(`Posts API returned ${response.status} for ${ticker}`);
        setPosts([]);
        return;
      }
      const data = await response.json();
      if (data.success) {
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
      setPosts([]);
    } finally {
      setPostsLoading(false);
    }
  };

  const handleShareIdea = async () => {
    console.log('[DEBUG] Share button clicked');
    console.log('[DEBUG] newIdea:', newIdea);
    console.log('[DEBUG] newIdea trimmed length:', newIdea.trim().length);

    if (!newIdea.trim()) {
      console.log('[DEBUG] Empty idea, showing alert');
      alert('Please enter your idea!');
      return;
    }

    const user = auth.currentUser;
    console.log('[DEBUG] Current user:', user);
    console.log('[DEBUG] User logged in:', !!user);

    if (!user) {
      console.log('[DEBUG] No user logged in, showing alert');
      alert('Please login to share ideas!');
      return;
    }

    // Check if user has permission to post
    if (!canCreatePost(userRole)) {
      console.log('[DEBUG] User does not have permission to post. Role:', userRole);
      if (userRole === USER_ROLES.SUSPEND) {
        alert('Your account has been suspended. You cannot create posts.');
      } else if (userRole === USER_ROLES.GUEST) {
        alert('Guest accounts cannot create posts. Please contact an administrator to upgrade your account.');
      } else {
        alert('You do not have permission to create posts.');
      }
      return;
    }

    console.log('[DEBUG] User is logged in, attempting to save to Firebase...');
    console.log('[DEBUG] Ticker:', ticker);
    console.log('[DEBUG] Sentiment:', newIdeaSentiment);
    console.log('[DEBUG] User UID:', user.uid);
    console.log('[DEBUG] User displayName:', user.displayName);
    console.log('[DEBUG] User email:', user.email);
    console.log('[DEBUG] Database instance:', db);

    try {
      console.log('[DEBUG] Calling addDoc...');
      const postData = {
        authorId: user.uid,
        authorName: user.displayName || user.email,
        authorAvatar: userAvatar,
        content: newIdea,
        prediction: newIdeaSentiment,
        ticker: ticker.toUpperCase(),
        images: newPostImages,
        feeling: newPostFeeling,
        activity: newPostActivity,
        likes: [],
        comments: [],
        shares: 0,
        createdAt: serverTimestamp()
      };
      console.log('[DEBUG] Post data:', postData);

      const docRef = await addDoc(collection(db, 'posts'), postData);
      console.log('[DEBUG] Document written successfully with ID:', docRef.id);

      setNewIdea('');
      setNewIdeaSentiment('bullish');
      setNewPostImages([]);
      setNewPostFeeling('');
      setNewPostActivity('');
      setShowNewPost(false);
      console.log('[DEBUG] Reloading posts...');
      loadPosts();
      alert('Your idea has been shared!');
    } catch (error) {
      console.error('[ERROR] Error sharing idea:', error);
      console.error('[ERROR] Error code:', error.code);
      console.error('[ERROR] Error message:', error.message);
      console.error('[ERROR] Error stack:', error.stack);
      alert('Failed to share idea: ' + error.message);
    }
  };

  const handleLike = async (postId, likes) => {
    const user = auth.currentUser;
    if (!user) {
      alert('Please login to like posts!');
      return;
    }

    // Check if user has permission to like posts
    if (!canLikePost(userRole)) {
      alert('You do not have permission to like posts.');
      return;
    }

    try {
      const postRef = doc(db, 'posts', postId);
      const hasLiked = likes.includes(user.uid);
      if (hasLiked) {
        await updateDoc(postRef, { likes: arrayRemove(user.uid) });
      } else {
        await updateDoc(postRef, { likes: arrayUnion(user.uid) });
      }
      loadPosts();
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Just now';

    // Handle both Firestore Timestamp and ISO string
    let date;
    if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      return 'Just now';
    }

    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return Math.floor(seconds / 60) + 'm ago';
    if (seconds < 86400) return Math.floor(seconds / 3600) + 'h ago';
    return Math.floor(seconds / 86400) + 'd ago';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive':
        return 'text-green-600 bg-green-50';
      case 'negative':
        return 'text-red-600 bg-red-50';
      case 'neutral':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-blue-600 bg-blue-50';
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive':
        return <TrendingUp className="w-4 h-4" />;
      case 'negative':
        return <TrendingDown className="w-4 h-4" />;
      default:
        return null;
    }
  };

  if (error && !company) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header with company info */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>

          {company ? (
            <div className="w-full">
              {/* First Row: Ticker, Active badge on left | Price and Change on right */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-3">
                  {/* Company Logo */}
                  <CompanyLogo ticker={company.ticker} size="lg" />

                  <h1 className="text-5xl font-bold text-gray-900">{company.ticker}</h1>
                  {company.isActive && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      Active
                    </span>
                  )}
                  {auth.currentUser && (
                    <WatchlistButton
                      ticker={company.ticker}
                      userId={auth.currentUser.uid}
                      size="lg"
                    
                          userRole={userRole}
                        />
                  )}
                </div>

                {/* Price Display - Right Side */}
                {priceLoading ? (
                  <div className="animate-pulse text-right">
                    <div className="h-12 bg-gray-200 rounded w-48 mb-1"></div>
                    <div className="h-4 bg-gray-200 rounded w-32 ml-auto"></div>
                  </div>
                ) : price ? (
                  <div className="text-right">
                    <div className="flex items-center justify-end gap-2 mb-1">
                      <span className="text-5xl font-bold text-gray-900">
                        ${price.price.toFixed(2)}
                      </span>
                      <div className={`flex items-center gap-1 text-xl font-semibold ${
                        price.change >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {price.change >= 0 ? <TrendingUp className="w-6 h-6" /> : <TrendingDown className="w-6 h-6" />}
                        <span>({price.changePercent >= 0 ? '+' : ''}{price.changePercent.toFixed(2)}%)</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(price.lastUpdated).toLocaleString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      }).toUpperCase()}
                    </div>
                  </div>
                ) : null}
              </div>

              {/* Second Row: Company Name on left | High, Low, Open, Vol on right */}
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-2xl text-gray-700">{company.name}</h2>

                {!priceLoading && price && (
                  <div className="flex items-center gap-6 text-sm text-gray-700">
                    <span>High: <span className="font-medium">${price.high.toFixed(2)}</span></span>
                    <span>Low: <span className="font-medium">${price.low.toFixed(2)}</span></span>
                    <span>Open: <span className="font-medium">${price.open.toFixed(2)}</span></span>
                    <span>Vol: <span className="font-medium">{(price.volume / 1000000).toFixed(2)}M</span></span>
                  </div>
                )}
              </div>

              {/* Third Row: Sector, Industry, Market Cap */}
              <div className="flex items-center gap-6 text-sm text-gray-700">
                {company.sector && (
                  <span><span className="font-medium">Sector:</span> {company.sector}</span>
                )}
                {company.industry && (
                  <span><span className="font-medium">Industry:</span> {company.industry}</span>
                )}
                {company.marketCap && (
                  <span><span className="font-medium">Market Cap:</span> {company.marketCap}</span>
                )}
              </div>

              {/* Fourth Row: Description */}
              {company.description && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-sm text-gray-700 leading-relaxed">{company.description}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-64"></div>
            </div>
          )}
        </div>
      </div>

      {/* Chart Section */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <StockChart ticker={ticker} />
      </div>

      {/* News and Ideas Section - StockTwits Style Layout */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left Side: News Feed (1/3) */}
          <div className="col-span-4">
            <NewsFeed ticker={ticker} userRole={userRole} />
          </div>

          {/* Right Side: Share Ideas (2/3) */}
          <div className="col-span-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                Ideas & Discussions
              </h3>
            </div>

            {/* Share Idea Box - Only show for members and above */}
            {canCreatePost(userRole) && (
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <button
                  onClick={() => setShowNewPost(true)}
                  className="w-full text-left px-4 py-3 bg-gray-100 rounded-full text-gray-500 hover:bg-gray-200"
                >
                  Share your thoughts on {ticker}...
                </button>
              </div>
            )}

            {/* Ideas Feed */}
            <div className="space-y-4">
              {postsLoading ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <p className="text-gray-500">Loading ideas...</p>
                </div>
              ) : posts.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <p className="text-gray-500">
                    Be the first to share your idea about {ticker}!
                  </p>
                </div>
              ) : (
                posts.map((post) => (
                  <article key={post.id} className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-start gap-3">
                      <button
                        onClick={() => handleAvatarClick(post.authorId)}
                        className="text-3xl hover:opacity-80 transition-opacity cursor-pointer"
                        title={`View ${post.authorName}'s profile`}
                      >
                        {post.authorAvatar || '😊'}
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <button
                            onClick={() => handleAvatarClick(post.authorId)}
                            className="font-bold hover:text-blue-600 transition-colors cursor-pointer"
                          >
                            {post.authorName}
                          </button>
                          <span className="text-xs text-gray-500">{getTimeAgo(post.createdAt)}</span>
                          <span className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ' + (
                            post.prediction === 'bullish' ? 'text-green-600 bg-green-100' :
                            post.prediction === 'bearish' ? 'text-red-600 bg-red-100' :
                            'text-yellow-600 bg-yellow-100'
                          )}>
                            {post.prediction === 'bullish' ? <TrendingUp className="w-3 h-3" /> :
                             post.prediction === 'bearish' ? <TrendingDown className="w-3 h-3" /> :
                             <BarChart3 className="w-3 h-3" />}
                            {post.prediction === 'bullish' ? 'Bullish' :
                             post.prediction === 'bearish' ? 'Bearish' : 'Neutral'}
                          </span>
                        </div>
                        <p className="text-gray-700 mb-3 leading-relaxed">
                          {post.content}
                        </p>

                        {/* Feeling/Activity Tags */}
                        {(post.feeling || post.activity) && (
                          <div className="mb-2 flex gap-2">
                            {post.feeling && (
                              <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs flex items-center gap-1">
                                <Smile className="w-3 h-3" />
                                {post.feeling}
                              </span>
                            )}
                            {post.activity && (
                              <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs flex items-center gap-1">
                                <Activity className="w-3 h-3" />
                                {post.activity}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Images */}
                        {post.images && post.images.length > 0 && (
                          <div className={`mb-3 grid gap-2 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                            {post.images.map((img, index) => (
                              <img
                                key={index}
                                src={img}
                                alt={`Post image ${index + 1}`}
                                className="w-full rounded-lg object-cover"
                                style={{ maxHeight: post.images.length === 1 ? '400px' : '200px' }}
                              />
                            ))}
                          </div>
                        )}

                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <button
                            onClick={() => handleLike(post.id, post.likes || [])}
                            className={'flex items-center gap-1 hover:text-red-500 ' +
                              ((post.likes || []).includes(auth.currentUser?.uid) ? 'text-red-500' : '')
                            }
                          >
                            <Heart className={'w-4 h-4 ' +
                              ((post.likes || []).includes(auth.currentUser?.uid) ? 'fill-current' : '')
                            } />
                            <span>{(post.likes || []).length}</span>
                          </button>
                          <button className="flex items-center gap-1 hover:text-blue-500">
                            <MessageCircle className="w-4 h-4" />
                            <span>{(post.comments || []).length}</span>
                          </button>
                          <button className="flex items-center gap-1 hover:text-green-500">
                            <Share2 className="w-4 h-4" />
                            <span>{post.shares || 0}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User Profile Popup */}
      {showProfilePopup && selectedUserId && (
        <UserProfilePopup
          userId={selectedUserId}
          currentUserId={auth.currentUser?.uid}
          onClose={handleCloseProfilePopup}
        />
      )}

      {/* New Post Modal */}
      {showNewPost && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowNewPost(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-4 relative" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">게시물 작성</h2>
              <button
                onClick={() => setShowNewPost(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {/* Top Section: User Info and Stock/Prediction Selection */}
              <div className="flex gap-4 mb-4">
                {/* User Info */}
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
                    <span className="text-3xl">{userAvatar}</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-base">{auth.currentUser?.displayName || auth.currentUser?.email}</h4>
                    <p className="text-sm text-gray-500">공개 게시물</p>
                  </div>
                </div>

                {/* Stock Display and Prediction */}
                <div className="flex gap-8 ml-auto">
                  <div>
                    <div className="px-2 py-1 text-sm border border-gray-300 rounded-lg bg-gray-50">
                      {ticker}
                    </div>
                  </div>

                  <div>
                    <select
                      value={newIdeaSentiment}
                      onChange={(e) => setNewIdeaSentiment(e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="bullish">🟢 강세</option>
                      <option value="neutral">🟡 중립</option>
                      <option value="bearish">🔴 약세</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Post Content */}
              <textarea
                value={newIdea}
                onChange={(e) => setNewIdea(e.target.value)}
                placeholder="무슨 생각을 하고 계신가요?"
                className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 leading-relaxed resize-none"
                rows="6"
                autoFocus
              />

              {/* Image Preview */}
              {newPostImages.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {newPostImages.map((img, index) => (
                    <div key={index} className="relative">
                      <img src={img} alt={`Upload ${index + 1}`} className="w-full h-48 object-cover rounded-lg" />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1 bg-gray-800 bg-opacity-70 text-white rounded-full hover:bg-opacity-90"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Feeling/Activity Display */}
              {(newPostFeeling || newPostActivity) && (
                <div className="mt-3 flex gap-2">
                  {newPostFeeling && (
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm flex items-center gap-1">
                      <Smile className="w-4 h-4" />
                      {newPostFeeling}
                      <button onClick={() => setNewPostFeeling('')} className="ml-1 hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {newPostActivity && (
                    <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm flex items-center gap-1">
                      <Activity className="w-4 h-4" />
                      {newPostActivity}
                      <button onClick={() => setNewPostActivity('')} className="ml-1 hover:text-green-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              )}

              {/* Add to Post Options */}
              <div className="mt-4 p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">게시물에 추가:</span>
                  <div className="flex gap-2">
                    {/* Photo/GIF Upload */}
                    <label className="cursor-pointer p-2 hover:bg-gray-100 rounded-lg transition" title="사진/GIF">
                      <Image className="w-5 h-5 text-green-600" />
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                    </label>

                    {/* Feeling */}
                    <button
                      onClick={() => {
                        setShowFeelingPicker(!showFeelingPicker);
                        setShowActivityPicker(false);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition"
                      title="기분"
                    >
                      <Smile className="w-5 h-5 text-yellow-600" />
                    </button>

                    {/* Activity */}
                    <button
                      onClick={() => {
                        setShowActivityPicker(!showActivityPicker);
                        setShowFeelingPicker(false);
                      }}
                      className="p-2 hover:bg-gray-100 rounded-lg transition"
                      title="활동"
                    >
                      <Activity className="w-5 h-5 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Feeling Picker Dropdown - Centered in Modal */}
            {showFeelingPicker && (
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 z-30">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">기분을 선택하세요</h4>
                <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                  {feelings.map((feeling) => (
                    <button
                      key={feeling.label}
                      onClick={() => {
                        setNewPostFeeling(`${feeling.emoji} ${feeling.label}`);
                        setShowFeelingPicker(false);
                      }}
                      className="flex items-center gap-2 p-3 hover:bg-gray-100 rounded-lg transition text-left"
                    >
                      <span className="text-2xl">{feeling.emoji}</span>
                      <span className="text-sm">{feeling.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Activity Picker Dropdown - Centered in Modal */}
            {showActivityPicker && (
              <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 p-4 z-30">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">활동을 선택하세요</h4>
                <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                  {activities.map((activity) => (
                    <button
                      key={activity.label}
                      onClick={() => {
                        setNewPostActivity(`${activity.emoji} ${activity.label}`);
                        setShowActivityPicker(false);
                      }}
                      className="flex items-center gap-2 p-3 hover:bg-gray-100 rounded-lg transition text-left"
                    >
                      <span className="text-2xl">{activity.emoji}</span>
                      <span className="text-sm">{activity.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowNewPost(false)}
                className="px-6 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition font-medium"
              >
                취소
              </button>
              <button
                onClick={handleShareIdea}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                게시
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
